import {
  shouldShowRiskFlowDirectionalForecast,
  type RiskFlowAlert,
  type RiskFlowDirectionalForecastInput,
} from "./riskflow-feed";

export interface RiskFlowEventKpi {
  label: string;
  value: string;
  score: number | null;
  kind: "fuse" | "detail";
}

export function riskFlowEventKpis(
  alert: Pick<
    RiskFlowAlert,
    | "subScores"
    | "direction"
    | "ivScore"
    | "headlineWeight"
    | "headlineIvScore"
    | "headlineEventWeight"
    | "headlineImpactWeight"
    | "headlineFreshness"
    | "vixAtPrint"
  > &
    Partial<RiskFlowDirectionalForecastInput>,
  limit = 4,
  showDirectionalScoring = true,
): RiskFlowEventKpi[] {
  const kpis: RiskFlowEventKpi[] = [];
  const sub = alert.subScores ?? null;
  const eventWeight = normalizedScore(
    alert.headlineEventWeight ?? sub?.eventWeight,
  );
  const narrativeSignal = normalizedScore(
    sub?.narrativeSignal ??
      alert.headlineWeight ??
      alert.headlineIvScore ??
      alert.headlineImpactWeight ??
      alert.ivScore,
  );

  addScore(kpis, "EW", eventWeight);
  addScore(kpis, "NS", narrativeSignal);

  kpis.push({
    label: "Bias",
    value: biasLabel(sub?.bias ?? alert.direction, {
      ...alert,
      showDirectionalScoring,
    }),
    score: null,
    kind: "detail",
  });
  kpis.push({
    label: "VIX_PRINT",
    value: formatVixAtPrint(alert, eventWeight),
    score: null,
    kind: "detail",
  });

  if (kpis.filter((kpi) => kpi.kind === "fuse").length === 0) {
    addScore(kpis, "Impact", normalizedScore(alert.headlineImpactWeight));
    addFreshness(kpis, alert.headlineFreshness);
  }
  return kpis.slice(0, limit);
}

function addScore(
  kpis: RiskFlowEventKpi[],
  label: string,
  value: number | null | undefined,
) {
  if (typeof value !== "number" || !Number.isFinite(value)) return;
  const score = clampScore(value);
  kpis.push({ label, value: formatScore(score), score, kind: "fuse" });
}

function addFreshness(
  kpis: RiskFlowEventKpi[],
  value: number | null | undefined,
) {
  if (typeof value !== "number" || !Number.isFinite(value)) return;
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  kpis.push({
    label: "Fresh",
    value: `${pct}%`,
    score: pct / 10,
    kind: "fuse",
  });
}

function formatScore(value: number): string {
  const magnitude = Math.abs(value);
  if (magnitude >= 10) return value.toFixed(0);
  if (magnitude >= 1) return value.toFixed(1).replace(/\.0$/, "");
  return value.toFixed(2).replace(/0$/, "");
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(10, value));
}

function normalizedScore(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return clampScore(value > 10 ? value / 10 : value);
}

function biasLabel(
  direction:
    | RiskFlowAlert["direction"]
    | NonNullable<RiskFlowAlert["subScores"]>["bias"]
    | null
    | undefined,
  alert: Pick<RiskFlowAlert, "ivScore" | "headlineIvScore" | "headlineWeight"> &
    Partial<RiskFlowDirectionalForecastInput>,
): string {
  if (!shouldShowRiskFlowDirectionalForecast(alert)) return "None";
  if (direction === "Bullish" || direction === "Bearish") return direction;
  return "None";
}

function formatVixAtPrint(
  alert: Pick<RiskFlowAlert, "subScores" | "vixAtPrint">,
  eventWeight: number | null,
): string {
  const sub = alert.subScores as
    | (RiskFlowAlert["subScores"] & {
        vixAtPrint?: unknown;
        vixAtTime?: unknown;
      })
    | null
    | undefined;
  const vixAtPrint = numericValue(
    alert.vixAtPrint ?? sub?.vixAtPrint ?? sub?.vixAtTime,
  );
  if (vixAtPrint != null) {
    return `${vixAtPrint.toFixed(2)}${shouldShowUnchanged(eventWeight) ? " (Unchanged)" : ""}`;
  }
  return shouldShowUnchanged(eventWeight) ? "00.00 (Unchanged)" : "00.00";
}

function shouldShowUnchanged(eventWeight: number | null): boolean {
  return eventWeight != null && eventWeight < 7.5;
}

function numericValue(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}
